import { createApi } from "@reduxjs/toolkit/query/react";
import { apiConstants, getGeneric } from "../common/constants";
import baseQuery from "./baseQuery";
import getShipmentsByParallelRequests from "./queries/parallel_shipments";
import getOrdersByDateRange, { getAllIntransitOrders, getAllNotPickedOrders, getAwbRegistedBuyDateRange, getDeliveredBuyDateRange, getRTODliveredByDateRange } from "./queries/get_shipments_by_date_range";
import getShipmentWithPickupAndDeliveryData from "./queries/get_shipments_with_pickup_delivery_data";

const excludes =
  "merchant,courier,pickupDetails.from_name,pickupDetails.from_phone_number,pickupDetails.from_address,pickupDetails.from_country,pickupDetails.from_email,pickupDetails.gstin,orderId,skipRule,giftwrap_charge,transactionCharge,clientOrderId,overriddenCourierRule,actualCourierProviderName,giftwrapCharge,overriddenCourierRule,transactionCharge,skipRule,createdDate,lastModifiedDate,createdByUser,modifiedByUser,merchantId,skipRule,version,overriddenCourierRule,dispatchMode,channelName,invoiceNumber,transactionCharge,giftwrapCharge,securityKey,invoiceNumber,channelType,invoiceUrl,labelUrl,referenceNumber,securityKey,sortCode,mongoInvoiceSignedUrlDetails,mongoLabelSignedUrlDetails,shipmentDetail,customerDetail,merchant,orderManifest";

export const commonRTK = createApi({
  reducerPath: "commonRTK",
  baseQuery: baseQuery,

  // cache expiry time in seconds
  keepUnusedDataFor: 2 * 30 * 60,
  tagTypes: ["ReleaseNotes", "ReleaseNotesUnread"],
  endpoints: (builder) => ({
    getRTODashboard: builder.query({
      query: (data) => ({ url: apiConstants.AGG_ORDER_BY_STATUS_CUSTOM, data }),
    }),
    getOrderItemWeight: builder.query({
      query: (data) => ({ url: apiConstants.AGG_ORDER_ITEM_WEIGHT, data }),
    }),
    getOrdersGroupByStates: builder.query({
      query: (data) => ({ url: apiConstants.AGG_ORDER_BY_STATE, data }),
    }),
    getOrdersGroupByZone: builder.query({
      query: (data) => ({ url: apiConstants.AGG_ORDER_ZONE, data }),
    }),
    getOrdersGroupByPaymentMode: builder.query({
      query: (data) => ({ url: apiConstants.AGG_ORDER_BY_PAYMENT_MODE, data }),
    }),
    getOrdersGroupByPickupPincodes: builder.query({
      query: (data) => ({
        url: apiConstants.AGG_ORDER_BY_PICKUP_PINCODE,
        data,
      }),
    }),
    getOrdersGroupByStatus: builder.query({
      query: (data) => ({ url: apiConstants.AGG_ORDER_BY_STATUS, data }),
    }),
    getNDROrdersGroupByStatus: builder.query({
      query: (data) => ({ url: apiConstants.AGGREGATE_NDR_STATUS, data }),
    }),
    getOrdersGroupByCourier: builder.query({
      query: (data) => ({
        url: apiConstants.AGG_ORDERS_DAY_WISE_COURIER,
        data,
      }),
    }),
    getShipmentsData: builder.query({
      query: (data) => ({
        url: apiConstants.ORDER_LISTS,
        data: {
          excludes: excludes,
          andfilter: [
            { field: "awbRegisteredDate", operator: "gte", value: data.from_date },
            { field: "awbRegisteredDate", operator: "lte", value: data.to_date },
            { field: "orderSubStatus", operator: "ne", value: "UNPROCESSED" },
            ...(data?.filters || []),
          ],
          offset: 0,
          limit: 15000,
        },
        type: "POST",
      }),
    }),
    getRTOGroupedByCourier: builder.query({
      query: (data) => ({ url: apiConstants.AGG_RTO_BY_COURIER, data }),
    }),
    getOrdersByStatusCode: builder.query({
      query: (data) => ({ url: apiConstants.AGG_ORDER_BY_STATUS_CUSTOM, data }),
    }),
    getMerchant: builder.query({
      query: ({ id }) => ({ url: `${apiConstants.GET_MERCHANT}/${id}` }),
    }),
    getMerchantDetails: builder.query({
      query: ({ id }) => ({ url: `${apiConstants.MERCHANT_DETAILS}/${id}` }),
    }),
    getAllReportTypes: builder.query({
      query: (data) => ({ url: apiConstants.GET_ALL_REPORT_TYPES, data }),
      transformResponse: (response) => {
        return response.responseObj;
      },
    }),
    getReportTypeDetails: builder.query({
      query: () => ({ url: apiConstants.GET_REPORT_TYPE_DETAILS }),
      transformResponse: (response) => {
        return response.responseObj;
      },
    }),
    getShipmentsInParallel: builder.query({
      queryFn: getShipmentsByParallelRequests,
    }),
    getPaginatedShipments: builder.query({
      query: (data) => ({ url: apiConstants.ORDER_LISTS, data, type: "POST" }),
    }),
    getShipmentDetail: builder.query({
      query: (data) => ({ url: apiConstants.ORDER_LISTS, data, type: "POST" }),
    }),
    getManifestSearch: builder.query({
      query: (data) => ({ url: apiConstants.MANIFEST_LISTS, data, type: "POST" }),
    }),
    getAllCourierPartners: builder.query({
      query: (data) => ({ url: apiConstants.COURIER_LIST, data, type: "POST" }),
      transformResponse: (res) => res.data
    }),
    // The merchant's connected sales channels, derived from the stores their orders arrive from.
    // Used by label configuration to scope settings to channels that actually exist for them.
    getUniqueStoreNames: builder.query({
      query: (merchantOid) => ({ url: apiConstants.GET_UNIQUE_STORE_NAMES, data: { merchantOid } }),
      transformResponse: (res) => res.data,
    }),
    getAllCourierPartnersCached: builder.query({
      query: (data) => ({ url: apiConstants.COURIER_LIST_CACHED, data, type: "POST" }),
      transformResponse: (res) => res.data
    }),
    getChannelOrder: builder.query({
      query: (data) => ({
        url: apiConstants.CHANNEL_ORDER_SEARCh,
        data,
        type: "POST",
      }),
    }),
    // Archived orders come back wrapped ({channelOrder, archivedAt, ...}); flatten so rows look like channel orders.
    getArchivedChannelOrders: builder.query({
      query: (data) => ({
        url: apiConstants.ARCHIVED_CHANNEL_ORDER_SEARCH,
        data,
        type: "POST",
      }),
      transformResponse: (res) => ({
        ...res,
        data: (res?.data || []).map(({ channelOrder, ...archive }) => ({ ...channelOrder, ...archive })),
      }),
    }),
    getManifestOrders: builder.query({
      query: (data) => ({
        url: apiConstants.MANIFEST_SEARCH_ORDERS,
        data,
        type: "POST",
      }),
    }),
    getNDRData: builder.query({
      query: (data) => ({
        url: apiConstants.NDR_LISTS,
        data,
        type: "POST",
      }),
    }),
    getNPRData: builder.query({
      query: (data) => ({
        url: apiConstants.NPR_LISTS,
        data,
        type: "POST",
      }),
    }),
    getForwardDelay: builder.query({
      query: (data) => {
        return {
          url: apiConstants.CONTROL_TOWER_DELIVERY_PENDING,
          data: {
            merchant_list: data.merchant,
            shipmentType_list: data.shipment_type,
            orderType_list: data.order_type
          },
          type: "GET",
        };
      },
    }),
    getLost: builder.query({
      query: (data) => {
        return {
          url: apiConstants.CONTROL_TOWER_LOST,
          data: {
            merchant_list: data.merchant,
            shipmentType_list: data.shipment_type,
            from_date: data.from_date,
            to_date: data.to_date,
          },
          type: "GET",
        };
      },
    }),
    getRtoDelay: builder.query({
      query: (data) => {
        return {
          url: apiConstants.CONTROL_TOWER_RTO_PENDING,
          data: {
            merchant_list: data.merchant,
            shipmentType_list: data.shipment_type,
            orderType_list: data.order_type
          },
          type: "GET",
        };
      },
    }),
    getPickUpDelay: builder.query({
      query: (data) => {
        return {
          url: apiConstants.CONTROL_TOWER_PICKUP_PENDING,
          data: {
            merchant_list: data.merchant,
            shipmentType_list: data.shipment_type,
            orderType_list: data.order_type
          },
          type: "GET",
        };
      },
    }),
    getAllAwbRegisteredByDateRange: builder.query({
      queryFn: getAwbRegistedBuyDateRange
    }),
    getOrderTransactionByDateRange: builder.query({
      query: (data) => ({ url: apiConstants.ORDER_TRANSACTION, data }),
    }),
    getAllDeliveredOrdersByDateRange: builder.query({
      queryFn: getDeliveredBuyDateRange
    }),
    getAllRTODeliveredOrdersByDateRange: builder.query({
      queryFn: getRTODliveredByDateRange
    }),
    getAllInTransitOrders: builder.query({
      queryFn: getAllIntransitOrders
    }),
    getAllNotPickedOrders: builder.query({
      queryFn: getAllNotPickedOrders
    }),
    getOrdersByMultipleDates: builder.query({
      queryFn: getOrdersByDateRange
    }),
    genericPostRequest: builder.query({
      query: (data) => ({ ...data })
    }),
    getPtlPur: builder.query({
      query: (data) => ({ url: apiConstants.PTL_PUR_SEARCH, data, type: "POST" }),
      transformResponse: (res) => res.data
    }),
    getSearchMerchant: builder.query({
      query: (data) => ({ url: apiConstants.MERCHANT_SEARCH, data, type: "POST" }),
      transformResponse: (res) => res.data
    }),
    getAllOrders: builder.query({
      query: (data) => ({ url: apiConstants.ORDER_LISTS, data, type: "POST" }),
      transformResponse: (res) => res
    }),
    getAllShipmentsWithPickupAndDeliveryData: builder.query({
      queryFn: getShipmentWithPickupAndDeliveryData
    }),
    getChannelOrderCountUF: builder.query({
      query: (data) => ({
        url: apiConstants.CHANNEL_ORDER_SEARCh,
        data: { "andfilter": data, "countOnly": true },
        type: "POST",
      }),
      transformResponse: (res) => res.count
    }),
    getChannelOrderCountFU: builder.query({
      query: (pay) => {
        console.log(pay);
        return {
          url: apiConstants.CHANNEL_ORDER_SEARCh, data: {
            "andfilter": pay,
            "countOnly": true
          },
          type: "POST",
        }
      },
      transformResponse: (res) => res.count
    }),
    getChannelOrderCountFUOutSide: builder.query({
      query: (pay) => {
        return {
          url: apiConstants.CHANNEL_ORDER_SEARCh, data: {
            "andfilter": pay,
            "countOnly": true
          },
          type: "POST",
        }
      },
      transformResponse: (res) => res.count
    }),
    getStatusWiseWeightDiscrepancy: builder.query({
      query: (pay) => {
        return {
          url: apiConstants.GET_WEIGHT_DISCREPANCY_ANALYTICS, data: pay,
          type: "POST",
        }
      },
      transformResponse: (res) => res?.data
    }),
    getB2BOdaCheck: builder.query({
      query: (params) => ({
        url: apiConstants.B2B_ODA_CHECK,
        data: params,
        type: "GET",
      }),
      transformResponse: (res) => res?.responseObj ?? res?.data?.responseObj ?? res,
    }),
    // Of the given couriers (prozoIds, comma separated), those the PTL Serviceability lists allow for a lane
    getPtlAllowedCouriers: builder.query({
      query: (params) => ({
        url: apiConstants.B2B_PTL_ALLOWED_COURIERS,
        data: params,
        type: "GET",
      }),
      transformResponse: (res) => res?.providers ?? res?.data?.providers ?? [],
    }),
    /* ─────────────── Release Notes ─────────────── */
    getReleaseNotes: builder.query({
      query: (params = {}) => ({
        url: apiConstants.RELEASE_NOTES,
        data: { limit: params.limit ?? 50, ...(params.before ? { before: params.before } : {}) },
        type: 'GET',
      }),
      transformResponse: (res) => ({
        items: Array.isArray(res?.items)
          ? res.items
          : Array.isArray(res?.data) ? res.data
            : Array.isArray(res?.notes) ? res.notes
              : Array.isArray(res) ? res
                : [],
        count: res?.count ?? 0,
      }),
      providesTags: ['ReleaseNotes'],
    }),
    getReleaseNotesUnreadCount: builder.query({
      query: () => ({
        url: apiConstants.RELEASE_NOTES_UNREAD,
        type: 'GET',
      }),
      transformResponse: (res) => {
        const c = res?.count ?? res?.unreadCount ?? res ?? 0;
        return typeof c === 'number' ? c : Number(c) || 0;
      },
      providesTags: ['ReleaseNotesUnread'],
    }),
    markReleaseNotesSeen: builder.mutation({
      query: () => ({
        url: apiConstants.RELEASE_NOTES_SEEN,
        data: undefined,
        type: 'POST',
      }),
      invalidatesTags: ['ReleaseNotesUnread'],
    }),
    // CT Brand/Courier Health source — fans out to the 6 freightsnapshot endpoints
    // for a given shipmentType (B2C / PTL) and returns them keyed by endpoint name.
    // Cached/deduped/shared by RTK Query (arg = shipmentType).
    getHealthSnapshots: builder.query({
      async queryFn(shipmentType) {
        // Live current-pipeline snapshot (matches CT's Brand/Courier Health matrix —
        // pending now + completed today). Date-ranged analysis lives in the "View" detail.
        const base = "external/ct/controltower/v1/wms/freightsnapshot/";
        const names = [
          "all_pickup_pending_orders_grouped_by_mechant",
          "all_pickedup_orders_today_grouped_by_mechant",
          "all_delivery_pending_orders_grouped_by_mechant",
          "all_delivered_orders_today_grouped_by_mechant",
          "all_rto_delivery_pending_orders_grouped_by_mechant",
          "all_rto_delivered_orders_today_grouped_by_mechant",
        ];
        try {
          const results = await Promise.all(
            names.map((n) =>
              getGeneric(base + n, { shipmentType_list: shipmentType })
                .then((r) => r?.data)
                .catch(() => null)
            )
          );
          const out = {};
          names.forEach((n, i) => {
            const d = results[i];
            out[n] = Array.isArray(d) ? d : d?.data || d?.result || [];
          });
          return { data: out };
        } catch (err) {
          return { error: { status: "FETCH_ERROR", error: String(err) } };
        }
      },
    }),
    // Live count of SLA-breached, Non-ABD PTL (B2B) shipments still in the pipeline.
    // PTL overview live stats from the 3 pending freightsnapshot endpoints (B2B), filtered
    // to this merchant by _id.merchant client-side (these grouped_by_mechant endpoints take
    // shipmentType_list only — they don't honor merchant_list). Returns:
    //   breached    — Non-ABD orders in SLA-breached groups (isBreached) — same signal as Health.
    //   stuckNonAbd — Non-ABD orders not updated in the last 24h (live "stuck", in-pipeline).
    // ABD stuck (only first-EDD-breached) is NOT computed here — that needs a backend EDD
    // field the snapshot doesn't expose (see docs/NEW_APIS_NEEDED.md).
    getPtlOverviewStats: builder.query({
      async queryFn(merchantId) {
        const base = "external/ct/controltower/v1/wms/freightsnapshot/";
        const names = [
          "all_pickup_pending_orders_grouped_by_mechant",
          "all_delivery_pending_orders_grouped_by_mechant",
          "all_rto_delivery_pending_orders_grouped_by_mechant",
        ];
        const STUCK_MS = 24 * 60 * 60 * 1000;
        try {
          const results = await Promise.all(
            names.map((n) =>
              getGeneric(base + n, { shipmentType_list: "B2B" })
                .then((r) => r?.data)
                .catch(() => null)
            )
          );
          const now = Date.now();
          let breached = 0;
          let stuckNonAbd = 0;
          results.forEach((d) => {
            const groups = Array.isArray(d) ? d : d?.data || [];
            groups.forEach((g) => {
              if (g?._id?.merchant !== merchantId) return;
              const orders = g.data || [];
              if (g?._id?.isBreached) {
                orders.forEach((o) => {
                  if (o?.appointdeliveryRequired === false) breached += 1;
                });
              }
              orders.forEach((o) => {
                if (o?.appointdeliveryRequired === false && o?.lastStatusUpdateTime) {
                  const t = new Date(o.lastStatusUpdateTime).getTime();
                  if (!isNaN(t) && now - t >= STUCK_MS) stuckNonAbd += 1;
                }
              });
            });
          });
          return { data: { breached, stuckNonAbd } };
        } catch (err) {
          return { error: { status: "FETCH_ERROR", error: String(err) } };
        }
      },
    }),
  }),
});

export const {
  useGetRTODashboardQuery,
  useGetOrderItemWeightQuery,
  useGetOrdersGroupByStatesQuery,
  useGetOrdersGroupByPaymentModeQuery,
  useGetOrdersGroupByPickupPincodesQuery,
  useGetOrdersGroupByStatusQuery,
  useGetOrdersGroupByZoneQuery,
  useGetShipmentsDataQuery,
  useGetOrdersGroupByCourierQuery,
  useGetRTOGroupedByCourierQuery,
  useGetOrdersByStatusCodeQuery,
  useGetMerchantQuery,
  useGetAllReportTypesQuery,
  useGetReportTypeDetailsQuery,
  useGetShipmentsInParallelQuery,
  useLazyGetPaginatedShipmentsQuery,
  useGetPaginatedShipmentsQuery,
  useGetShipmentDetailQuery,
  useLazyGetManifestSearchQuery,
  useLazyGetShipmentDetailQuery,
  useLazyGetChannelOrderQuery,
  useGetChannelOrderQuery,
  useGetArchivedChannelOrdersQuery,
  useGetForwardDelayQuery,
  useGetPickUpDelayQuery,
  useGetRtoDelayQuery,
  useGetOrderTransactionByDateRangeQuery,
  useGetAllAwbRegisteredByDateRangeQuery,
  useGetAllDeliveredOrdersByDateRangeQuery,
  useGetAllRTODeliveredOrdersByDateRangeQuery,
  useGetAllInTransitOrdersQuery,
  useGetAllNotPickedOrdersQuery,
  useGetOrdersByMultipleDatesQuery,
  useGetManifestOrdersQuery,
  useGetAllCourierPartnersQuery,
  useGetAllCourierPartnersCachedQuery,
  useGetUniqueStoreNamesQuery,
  useGetNDROrdersGroupByStatusQuery,
  useGetNDRDataQuery,
  useLazyGetNDRDataQuery,
  useGetNPRDataQuery,
  useGenericPostRequestQuery,
  useGetPtlPurQuery,
  useGetSearchMerchantQuery,
  useGetLostQuery,
  useGetAllOrdersQuery,
  useGetMerchantDetailsQuery,
  useGetAllShipmentsWithPickupAndDeliveryDataQuery,
  useGetChannelOrderCountFUQuery,
  useGetChannelOrderCountUFQuery,
  useGetChannelOrderCountFUOutSideQuery,
  useLazyGetStatusWiseWeightDiscrepancyQuery,
  useGetStatusWiseWeightDiscrepancyQuery,
  useGetB2BOdaCheckQuery,
  useLazyGetB2BOdaCheckQuery,
  useGetPtlAllowedCouriersQuery,
  useGetReleaseNotesQuery,
  useGetReleaseNotesUnreadCountQuery,
  useMarkReleaseNotesSeenMutation,
  useGetHealthSnapshotsQuery,
  useGetPtlOverviewStatsQuery,
} = commonRTK;
